import { ThunkAction } from 'redux-thunk';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { DraftCompanyService } from '$/services';
import { companiesClearAction, setCompanyAction } from '$/store/company/actions';
import {
  DRAFT_FAILURE,
  DRAFT_LOADING,
  UPDATE_DRAFT_FAILURE,
  UPDATE_DRAFT_INPROGRESS,
  UPDATE_DRAFT_SUCCESS,
  CREATE_DRAFT_INPROGRESS,
  CREATE_DRAFT_FAILURE,
  CREATE_DRAFT_SUCCESS,
  SET_DRAFT_COMPANY,
  DELETE_DRAFT_INPROGRESS,
  DELETE_DRAFT_FAILURE,
  DELETE_DRAFT_SUCCESS,
  PUBLISH_DRAFT_INPROGRESS,
  PUBLISH_DRAFT_FAILURE,
  PUBLISH_DRAFT_SUCCESS,
  SET_DRAFT_INPROGRESS,
  SET_DISCARD_DRAFT,
} from './types';
import { toastError, toastSuccess } from '$/store/toast/actions';

const draftLoadingAction = () => {
  return {
    type: DRAFT_LOADING,
  };
};

const draftFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: DRAFT_FAILURE,
    payload: error,
    error: true,
  };
};

const updateDraftInProgressAction = () => {
  return {
    type: UPDATE_DRAFT_INPROGRESS,
  };
};

const updateDraftSuccessAction = () => {
  return {
    type: UPDATE_DRAFT_SUCCESS,
  };
};

const updateDraftFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: UPDATE_DRAFT_FAILURE,
    payload: error,
    error: true,
  };
};

const createDraftInProgressAction = () => {
  return {
    type: CREATE_DRAFT_INPROGRESS,
  };
};

const createDraftSuccessAction = () => {
  return {
    type: CREATE_DRAFT_SUCCESS,
  };
};

const createDraftFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: CREATE_DRAFT_FAILURE,
    payload: error,
    error: true,
  };
};

const setDraftCompanyAction = (draftCompany?: Bixby.Models.DraftCompany) => {
  return {
    type: SET_DRAFT_COMPANY,
    payload: draftCompany,
  };
};

const deleteDraftInProgressAction = () => {
  return {
    type: DELETE_DRAFT_INPROGRESS,
  };
};

const deleteDraftSuccessAction = () => {
  return {
    type: DELETE_DRAFT_SUCCESS,
  };
};

const deleteDraftFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: DELETE_DRAFT_FAILURE,
    payload: error,
    error: true,
  };
};

const publishDraftInProgressAction = () => {
  return {
    type: PUBLISH_DRAFT_INPROGRESS,
  };
};

const publishDraftSuccessAction = () => {
  return {
    type: PUBLISH_DRAFT_SUCCESS,
  };
};

const publishDraftFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: PUBLISH_DRAFT_FAILURE,
    payload: error,
    error: true,
  };
};

const setDraftInProgressAction = (isEditing: boolean) => {
  return {
    type: SET_DRAFT_INPROGRESS,
    payload: isEditing,
  };
};

const setDiscardDraftInProgressAction = (discardDraft: boolean) => {
  return {
    type: SET_DISCARD_DRAFT,
    payload: discardDraft,
  };
};

// thunk actions
export const adminGetDraft =
  (companyId: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(draftLoadingAction());
    try {
      const company = await DraftCompanyService.adminGetById(companyId);
      dispatch(setDraftCompanyAction(company));
      await dispatch(setDraftInProgressAction(true));
    } catch (error: any) {
      await dispatch(draftFailureAction(error));
      await dispatch(setDraftInProgressAction(false));
    }
  };

export const createDraft =
  (companyId: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(createDraftInProgressAction());
    try {
      await DraftCompanyService.create(companyId);
      const draft = await DraftCompanyService.adminGetById(companyId);
      await dispatch(createDraftSuccessAction());
      await dispatch(setDraftCompanyAction(draft));
      await dispatch(setDraftInProgressAction(true));
    } catch (error: any) {
      dispatch(createDraftFailureAction(error));
      await dispatch(setDraftInProgressAction(false));
    }
  };

export const updateDraft =
  (
    companyId: string,
    updateRequest: Bixby.Models.JsonPatchOperation[]
  ): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(updateDraftInProgressAction());
    try {
      await DraftCompanyService.update(companyId, updateRequest);
      const draftCompany = await DraftCompanyService.adminGetById(companyId);
      await dispatch(updateDraftSuccessAction());
      await dispatch(adminGetDraft(draftCompany.id));
      await dispatch(toastSuccess('Draft saved'));
    } catch (error: any) {
      await dispatch(toastError('Failed to save draft'));
      dispatch(updateDraftFailureAction(error));
    }
  };

export const setDraftCompany =
  (companyId?: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(draftLoadingAction());
    try {
      const draftCompany = companyId ? await DraftCompanyService.adminGetById(companyId) : undefined;
      dispatch(setDraftCompanyAction(draftCompany));
    } catch (error: any) {
      dispatch(draftFailureAction(error));
    }
  };

export const deleteDraft =
  (companyId: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(deleteDraftInProgressAction());
    try {
      await DraftCompanyService.delete(companyId);
      await dispatch(deleteDraftSuccessAction());
      await dispatch(setDraftCompanyAction(undefined));
      await dispatch(setDiscardDraftInProgressAction(true));
    } catch (error: any) {
      dispatch(deleteDraftFailureAction(error));
    }
  };

export const publishDraft =
  (companyId: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(publishDraftInProgressAction());
    try {
      const company = await DraftCompanyService.publish(companyId);
      await dispatch(setCompanyAction(company));
      await dispatch(publishDraftSuccessAction());
      await dispatch(setDraftInProgressAction(false));
      await dispatch(companiesClearAction());
      await dispatch(toastSuccess('Your draft has been published'));
    } catch (error: any) {
      await dispatch(toastError('Failed to publish your draft'));
      dispatch(publishDraftFailureAction(error));
    }
  };

export const setDraftInProgress =
  (isEditing: boolean): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(setDraftInProgressAction(isEditing));
  };
